import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Collapse from "@mui/material/Collapse";

const getDynamicSx = (isMobile) => ({
  buttonSx: {
    width: "125px",
    height: "59px",
    color: "#000",
    opacity: "1",
    fontFamily: "Inter",
    fontSize: "24px",
    fontWeight: "600",
    borderRadius: "40px",
    padding: "0px",
    "&:hover": {
      background: "linear-gradient(180deg, rgba(255, 218, 11, 1.00) 0%, #FFB400 100%)",
    },
  },
  menuSx: {
    width: "auto",
    fontSize: "18px",
  },
});

export default function Navbar() {
  const isMobile = useMediaQuery("(max-width:1350px)");
  const { menuSx, buttonSx } = getDynamicSx(isMobile);
  const location = useLocation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [drawerWidth, setDrawerWidth] = useState("50%");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const handleToggleMenu = (menuId) => {
    setOpenMenu(openMenu === menuId ? null : menuId);
  };

  const handleOpen = (event, menuItemId) => {
    setIsClosing(false);
    setAnchorEl(event.currentTarget);
    setSelectedMenuItem(menuItemId);
  };

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handlePageLinkClick = useCallback(
    (e) => {
      const referType = e.currentTarget.getAttribute("data-refer");
      switch (referType) {
        case "url":
          window.location.href = e.currentTarget.getAttribute("data-path");
          break;
        case "page":
          navigate(`/${e.currentTarget.getAttribute("data-path")}?id=${e.currentTarget.getAttribute("data-id")}`);
          break;
        case "path": 
          window.location.href = `/${e.currentTarget.getAttribute("data-path")}`;
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, handleClose]
  );

  useEffect(() => {
    setTimeout(() => {
      if (isClosing) {
        setAnchorEl(null);
        setSelectedMenuItem(null);
      }
    }, 100);
  }, [isClosing]);

  useEffect(() => {
    axios
      .post("/api/website/getMenuList")
      .then((response) => {
        setMenuItems(response.data.data.menuListData);
      })
      .catch((error) => {
        console.error("Error fetching article content:", error);
      });

    function updateDrawerWidth() {
      if (window.innerWidth < 768) {
        setDrawerWidth("75%");
      } else {
        setDrawerWidth("50%");
      }
    }

    window.addEventListener("resize", updateDrawerWidth);
    updateDrawerWidth(); // Initialize on first render

    return () => {
      window.removeEventListener("resize", updateDrawerWidth);
    };
  }, []);

  return (
    <div className="nav">
      <div className="nav-wrap relative">
        <Link
          to="/"
          className="flex items-center pl-[48px] table:pl-[20px] mobile:pl-0 mobile:absolute mobile:left-1/2 mobile:top-1/2 mobile:-translate-x-1/2 mobile:-translate-y-1/2"
        >
          <img
            className="h-[80px] w-auto object-contain table:h-[64px] mobile:h-[72px]"
            src="/asset/image/index/logo.png"
            alt="安信國際資產管理顧問"
          />
        </Link>
        {isMobile ? (
          <>
            <IconButton
              color="inherit"
              size="large"
              aria-label="menu"
              onClick={handleDrawerOpen}
              sx={{ ml: "auto" }}
            >
              <MenuIcon
                className="text-white"
                sx={{
                  fontSize: {
                    xs: "35px",
                    sm: "40px",
                  },
                }}
              />
            </IconButton>
            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={handleDrawerClose}
              disableScrollLock={true}
              PaperProps={{
                style: {
                  top: "100px",
                  width: drawerWidth,
                },
              }}
            >
              <div className="list-warp" onMouseLeave={handleDrawerClose}>
                {location.pathname !== "/" && (
                  <div className="py-[10px] px-5">
                    <Link to={`/`}>
                      <span className="list-btn">回到首頁</span>
                    </Link>
                  </div>
                )}
                {menuItems.map((mainItem) => (
                  <div key={mainItem.id} className="py-[10px] px-5">
                    {mainItem.path != null ? (
                      <Link to={`/${mainItem.path}`} onClick={handleDrawerClose}>
                        <span className="list-btn">{mainItem.name}</span>
                      </Link>
                    ) : (
                      <span
                        className="list-btn cursor-pointer"
                        onClick={() => handleToggleMenu(mainItem.id)}
                      >
                        {mainItem.name}
                      </span>
                    )}
                    <Collapse in={openMenu === mainItem.id}>
                      {mainItem.pageList && mainItem.pageList.length > 0 && (
                        <div className="mt-[10px]">
                          {mainItem.pageList.map((page) => (
                            <div key={page.id} className="py-1 px-0 text-f6f4dd">
                              <button
                                data-refer={page.referType}
                                data-path={page.path}
                                data-id={page.id}
                                onClick={handlePageLinkClick}
                              >
                                {page.name}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </Collapse>
                  </div>
                ))}
              </div>
            </Drawer>
          </>
        ) : (
          <div className="list-warp">
            {location.pathname !== "/" && (
              <Button sx={buttonSx}>
                <Link to={`/`}>
                  <span className="list-btn">回到首頁</span>
                </Link>
              </Button>
            )}
            {menuItems.map((mainItem) => (
              <div key={mainItem.id} onMouseOver={(e) => handleOpen(e, mainItem.id)}>
                <Button
                  sx={buttonSx}
                  id={`fade-button-${mainItem.id}`}
                  aria-controls={`fade-menu-${mainItem.id}`}
                  aria-haspopup="true"
                  aria-expanded={Boolean(anchorEl)}
                >
                  {mainItem.path != null ? (
                    <Link to={`/${mainItem.path}`}>
                      <span className="list-btn">{mainItem.name}</span>
                    </Link>
                  ) : (
                    <span className="list-btn">{mainItem.name}</span>
                  )}
                </Button>
                {mainItem.pageList && mainItem.pageList.length > 0 && (
                  <Menu
                    id={`fade-menu-${mainItem.id}`}
                    anchorEl={anchorEl}
                    open={selectedMenuItem === mainItem.id}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                    disableScrollLock={true}
                    disableAutoFocus={true}
                    disableAutoFocusItem={true}
                    disableEnforceFocus={true}
                    disableRestoreFocus={true}
                    MenuListProps={{
                      autoFocusItem: false,
                      onMouseEnter: (e) => handleOpen(e, mainItem.id),
                      onMouseLeave: handleClose,
                      style: { pointerEvents: "auto" },
                    }}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {mainItem.pageList.map((page) => (
                      <MenuItem key={page.id} sx={menuSx} data-path={page.path} data-id={page.id}>
                        <button
                          data-refer={page.referType}
                          data-path={page.path}
                          data-id={page.id}
                          onClick={handlePageLinkClick}
                          onMouseLeave={handleClose}
                          className="w-full text-left"
                        >
                          {page.name}
                        </button>
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
